import type { FieldDefinition } from "@estelle/domain";

/**
 * The fields the Contacts module offers to the filter builder, the column
 * chooser and the sort control.
 *
 * The first six mirror FR-CON-12's search fields verbatim — name, company,
 * phone, email, GSTIN, tag — because the column chooser takes the first six
 * as the default set and those are exactly the fields someone scanning a
 * contact list needs to tell one row from another.
 *
 * `phone` and `email` show a contact's primary number and address, which the
 * list response carries on each row (#551). The filter is wider than the
 * column: it matches any number or address the contact holds, answered by the
 * server, so a row can match on a phone it does not show.
 */
export const CONTACT_FIELDS: FieldDefinition[] = [
  { name: "displayName", label: "Name", type: "text", sortable: true },
  { name: "companyName", label: "Company", type: "text", sortable: true },
  { name: "phone", label: "Phone", type: "text" },
  { name: "email", label: "Email", type: "text" },
  { name: "gstin", label: "GSTIN", type: "text" },
  { name: "tags", label: "Tags", type: "tag" },
  /**
   * Filters a contact by the types it carries (FR-CON-11). This is the
   * general-purpose escape hatch — "type has any of X, Y, and also tagged
   * Z" — for the case the standing per-section filters in
   * `screens/ContactList.tsx` don't cover on their own; those are the
   * primary way someone reaches Vendors or Suppliers. (The Customers section
   * is Shopify's customers now, #447 — a hand-kept contact of type Customer
   * is reached here, by this field.)
   *
   * `typeIds` holds ids, not names, because `Contact` carries no denormalised
   * type names to show or filter on — the same reason `has_any_tag`'s value
   * input falls back to free text below instead of a checkbox list: there is
   * no fixed `options` array to offer, since a user can rename or add a type
   * at any time from the Contact Types screen.
   */
  { name: "typeIds", label: "Type", type: "tag" },
  { name: "isArchived", label: "Archived", type: "boolean" },
  { name: "createdAt", label: "Created", type: "date", sortable: true },
];
